import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { SearchButton } from "../common/Button";
import { DatepickerField } from "../forms/inputGroup/helpers";
import { validateActivitySearch } from "../MainSearchForm/activity/helpers";
import { DestinationsPicker } from "../MainSearchForm/location";
import PaxPickerModal from "../MainSearchForm/modals/PaxPickerModal";
import { PaxPicker } from "../MainSearchForm/widgets";

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepActivitySearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepActivitySearchForm", {}),
  }),
  searchModal: (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.border_radius,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    padding: [0, jssVariables.normal_gap],
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),
  activitySearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(4, max-content)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.activitySearchForm", {}),
  }),
  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      width: "70rem",
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    activitySearchForm: (props) => ({
      gridTemplateRows: "unset",
      gridTemplateColumns: "repeat(3, 1fr) max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.activitySearchForm.smallscreen.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      margin: "unset",
      paddingBottom: "1px",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
});
const DeepActivitySearchForm = () => {
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { setShowActFormModal } = useContext(GlobalModalsContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepActivitySearchForm",
  });

  const search_form = useSelector((state) => state.ActivitySearchForm);
  const { date_from, date_to } = search_form;

  const classes = styles({ customStyles });

  const [form, setForm] = useState(search_form);

  function createPayload(form) {
    const payload = {
      in: form.datesArray[0],
      out: form.datesArray[1],
      adt: form.adults,
      chd: form.children,
      ages: form.children_ages,
      dst_c: form.destination_code,
      dst_m: form.destination_meta,
    };
    return payload;
  }

  async function onNewSearch(form) {
    const encoded_data = window.btoa(JSON.stringify(createPayload(form)));
    window.open(`/deep-activity-search/${encoded_data}`, "_blank");
    setShowActFormModal(false);
  }

  return (
    <div className={classes.DeepActivitySearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowActFormModal(false)}
          />
        </div>
        <Formik
          initialValues={{
            destinationObj: {
              destination_code: [],
              destination_meta: {},
            },
            datesArray: [date_from, date_to],
          }}
          validate={(values) => {
            const error_msgs = validateActivitySearch({ values });
            return error_msgs;
          }}
          onSubmit={async (values) => {
            onNewSearch({
              datesArray: values.datesArray,
              destination_code: values.destinationObj.destination_code,
              destination_meta: values.destinationObj.destination_meta,
              adults: form.adults,
              children: form.children,
              children_ages: form.children_ages,
            });
          }}
        >
          <Form className={classes.activitySearchForm}>
            <DestinationsPicker
              name="destinationObj"
              types={["DEST"]}
              product_type={"ACT"}
              placeholder={t("destinationPlaceholder")}
              autocomplete={true}
              value={_.get(form, "destination_code", [])}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <DatepickerField
              label={t("dates")}
              name="datesArray"
              selectsRange={true}
              dateFormat="MMM d, yyyy"
              minDate={DateTime.now().toISODate()}
              maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
              placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
              withPortal={true}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <PaxPicker
              adults={form.adults}
              childs={form.children}
              children_ages={form.children_ages}
              onClick={() => setShowPaxPickerModal(true)}
              product_type="ACT"
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <div className={classes.searchBtnContr}>
              <SearchButton
                type="submit"
                text={t("search")}
                loading={false}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <PaxPickerModal
          adults={form.adults}
          childs={form.children}
          children_ages={form.children_ages}
          onFormUpdate={({ form: paxData }) => {
            setForm({ ...form, ...paxData });
          }}
          onCancel={() => {
            toggleNoScroll(false);
            setShowPaxPickerModal(false);
          }}
        />
      )}
    </div>
  );
};
DeepActivitySearchForm.propTypes = {};
export default DeepActivitySearchForm;
